import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { countries, ui } from '@/constants';
import { makeFormField, getCountryByHostname } from '@/helpers';
import { FormField, CustomerProfile } from '@/models';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { AccountPageLoadEvent } from '@/services/analytics/events';
import { PageLoadEventType } from '@/services/analytics/platforms/adobe-analytics/models';

interface EditCountryForm {
    [key: string]: FormField;
}

@Component({
  name: 'CmgRepromptCountryProfileModal',
})
export default class CmgRepromptCountryProfileModal extends Vue {
    form: EditCountryForm = {
      country: makeFormField(),
    };

    isLoading = false;
    isFormValid = false;
  
    @Watch('visible', { immediate: true})
    visibleChanged(visible: boolean) {
      if (visible) {
        this.initializeForm();
      }
    }

    get customer(): CustomerProfile | null {
      return this.$appStore.state.customer.profile;
    }
    
    get isMobile(): boolean {
      return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
    }

    get visible(): boolean {
      return this.$appStore.state.ui.modals.repromptCountryProfileModal.visible;
    }
  
    get countryProfiles(): object[] {
      return Object.values(countries);
    }
  
    onSelectCountry(value: string): void {
      this.isFormValid = true;
      this.form.country.dirty = true;
      this.form.country.value = value;
    }
  
    initializeForm(): void {
      this.form.country.value = '';
      this.form.country.dirty = false;
      this.isFormValid = false;
    }
  
    async onUpdateCountry(): Promise<void> {
      try {
        this.isLoading = true;
        await this.$services.customer.updateCustomer({ country: this.form.country.value });
        this.close();
        await this.$services.customer.refreshCustomerProfileInfo();
        this.popExplainerModal();
      } catch (err) {
        this.$services.logger.error('failed to update customer profile country');
        this.close();
      }
    }
  
    close() {
      this.isLoading = false;
      this.$services.ui.setModalVisibility('repromptCountryProfileModal', false);
    }

    popExplainerModal(): void {
    // If the customer has not yet selected a country in profile yet, we will pop the explainer modal after forcing customer to choose country if still applicable
    // If the customer is a non-guest who hasn't enrolled in loyalty and they haven't opted out yet and we are in the US country view experience, pop the explainer modal
    // We won't pop modal for Canadians who are not eligible for joining loyalty
    // If the customer is enrolled in loyalty and has not yet seen the rewards general onboarding 2.0, show the RewardsGeneralOnboarding modal
    const countryProfile = getCountryByHostname(window.location.hostname);
    if (this.customer)
    {
      const { isGuest, country } = this.customer;
      const isEnrolledInLoyalty = this.$services.loyalty.isEnrolledInLoyalty(this.customer);
      const hasNotDeclinedLoyalty = !this.$appStore.state.customer.loyalty.declined;

      if (
        !isGuest &&
        !!country &&
        country != 'CA' &&
        countryProfile.countryCode =='US'
      ) {
        if (isEnrolledInLoyalty && !this.customer!.hasSeenRewardsGenOnboarding)
        {
          this.$services.ui.showOnboardingModal(this.$services.customer, this.customer ?? undefined);
        } 
        else if (!isEnrolledInLoyalty && hasNotDeclinedLoyalty)
        {
          this.$services.ui.setModalVisibility('loyaltyExplainer', true, { state: LoyaltyExplainerModalState.WelcomeUnenrolled });
          this.$services.ui.firstFocus();
        }
      }
    }
    }
}
